/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/**
 * Session-error signal.
 *
 * `http401UnauthorizedHandler` runs outside React (axios interceptor / RxJS
 * epics), so it can't render anything itself. Inside the app webview it raises
 * this signal instead of redirecting to the portal login page, and
 * `SessionErrorPage` — mounted in the app shell — renders the retryable error
 * screen. A plain listener set keeps `utils/network` free of a store import
 * (`store/store` already imports `utils/network`, so dispatching from here
 * would close an import cycle).
 *
 * The router's auth gate raises the same signal for a different failure, so
 * every raise carries a `SessionErrorReason`. The screen is identical either
 * way — the merchant's only move is Retry — but the telemetry must not report
 * a 401 that never happened.
 */

/**
 * Split by who has to act on it, not by which component raised it — the screen
 * is identical in every case, the owner is not.
 *
 * - `http401` — a request came back 401: the token was rejected mid-session.
 *   Raised by `utils/network`; the only one where a request was made at all.
 * - `missingProfile` — no usable credential of either kind at the auth gate: no
 *   readable profile under cookie auth and no token to fall back on. The app
 *   opened the webview without handing over a session.
 * - `invalidToken` — a token WAS handed over but is expired or undecodable, so
 *   the gate rejected it before any request. A stale credential, not an absent
 *   one.
 * - `missingCountry` — the profile is good enough to authenticate but carries
 *   no country, which the gate also requires. A profile-data gap, not an auth
 *   failure.
 * - `merchantDetailUnavailable` — the webview could not resolve the merchant
 *   detail required by an ecommerce route.
 *
 * `missingProfile` / `invalidToken` / `missingCountry` are all raised by the
 * router gate. Collapsing them into one value would put three different owners
 * behind a single label, which is what the reason exists to prevent.
 */
export type SessionErrorReason =
  | "http401"
  | "missingProfile"
  | "invalidToken"
  | "missingCountry"
  | "merchantDetailUnavailable"

type SessionErrorListener = (reason: SessionErrorReason) => void

const listeners = new Set<SessionErrorListener>()

// The signal latches: a 401 can land before the shell has mounted (bootstrap
// requests), and a lost signal would leave the merchant on a half-empty screen
// with no way forward. A late subscriber is notified immediately instead.
let raisedReason: SessionErrorReason | null = null

/**
 * Signals that the session is no longer usable and the app cannot recover on
 * its own.
 *
 * Only the first raise notifies. A dead session fails every in-flight request,
 * so a screen load can land here several times over; re-notifying would show
 * the same screen again and — because the subscriber reports to Datadog — count
 * one dead session as many, which is exactly the frequency the telemetry is
 * there to measure.
 */
export const emitSessionError = (reason: SessionErrorReason): void => {
  if (raisedReason !== null) return
  raisedReason = reason
  listeners.forEach(listener => listener(reason))
}

/**
 * Whether a session error has already been raised, read synchronously.
 *
 * `subscribeToSessionError` replays a latched signal, but only once its
 * subscribing effect has run, and only as a state update — one commit too late
 * for a caller that has to be right in the commit where the error is raised.
 * `useAppBottomNavVisibility` is that caller: the router's auth gate raises
 * from its own mount effect, and the bottom-nav value it sends the app in that
 * same commit decides whether the merchant can leave the screen.
 */
export const isSessionErrorRaised = (): boolean => raisedReason !== null

/** Subscribes to session errors. Returns the unsubscribe function. */
export const subscribeToSessionError = (
  listener: SessionErrorListener,
): (() => void) => {
  listeners.add(listener)
  if (raisedReason !== null) {
    listener(raisedReason)
  }
  return () => {
    listeners.delete(listener)
  }
}

/** Test-only: clears the latched signal between cases. */
export const resetSessionErrorForTesting = (): void => {
  raisedReason = null
  listeners.clear()
}
